import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormAlert } from "@/components/common/Alert";
import { useCatalogue } from "@/context/CatalogueContext";
import { api, errorMessage } from "@/lib/api";
import { agentName } from "@/lib/catalogue";
import { cn } from "@/lib/utils";

export const CHANNELS = ["LinkedIn", "X", "Instagram", "Facebook", "YouTube", "Reddit", "Quora", "Medium", "Substack", "Email", "Newsletter", "Landing page", "Event", "Paid ads", "PR", "WhatsApp", "Lists", "Reporting", "Other"];
const EMPTY = { title: "", text: "", for_id: "any", channels: [], campaign: "", why_it_worked: "", rating: null };

export const ForSelect = ({ value, onChange, id, includeAll = false, testid }) => {
  const { agents, workflows } = useCatalogue();
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger id={id} className="h-10 rounded-xl bg-card" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent className="max-h-80 rounded-xl">
        {includeAll && <SelectItem value="all">All agents</SelectItem>}
        <SelectItem value="any">Any agent</SelectItem>
        {workflows.map((w) => <SelectItem key={w.id} value={w.id}>{w.id} · {w.name}</SelectItem>)}
        {agents.filter((a) => !a.parent).map((a) => <SelectItem key={a.emp_no} value={a.emp_no}>{a.emp_no} · {agentName(a)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
};

export const PromptFormDialog = ({ open, onOpenChange, editing, defaultFor = "any", onSaved }) => {
  const [form, setForm] = useState(EMPTY);
  const [suggestion, setSuggestion] = useState("");
  const [tidying, setTidying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!open) return;
    setForm(editing ? { ...EMPTY, ...editing } : { ...EMPTY, for_id: defaultFor });
    setSuggestion("");
    setError("");
  }, [open, editing, defaultFor]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleChannel = (c) => set("channels", form.channels.includes(c) ? form.channels.filter((x) => x !== c) : [...form.channels, c]);

  const tidy = async () => {
    setTidying(true);
    setError("");
    try {
      const { data } = await api.post("/prompts/tidy", { text: form.text });
      setSuggestion(data.suggestion);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setTidying(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const body = { title: form.title, text: form.text, for_id: form.for_id, channels: form.channels, campaign: form.campaign, why_it_worked: form.why_it_worked, rating: form.rating };
    try {
      const { data } = editing ? await api.patch(`/prompts/${editing.id}`, body) : await api.post("/prompts", body);
      toast.success(editing ? "Prompt updated" : "Prompt saved to the hub");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto rounded-xl sm:max-w-2xl" data-testid="prompt-form-dialog">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit prompt" : "Save a prompt"}</DialogTitle>
          <DialogDescription data-testid="prompt-form-help">Do not include a client's or a contact's name or details. Prompts are shared with the whole team.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <FormAlert testid="prompt-form-error">{error}</FormAlert>
          <div className="space-y-2"><Label htmlFor="prompt-title">Title</Label><Input id="prompt-title" value={form.title} maxLength={120} onChange={(e) => set("title", e.target.value)} data-testid="prompt-title-input" /></div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="prompt-text">Prompt</Label>
              <Button type="button" variant="ghost" size="sm" onClick={tidy} disabled={tidying || !form.text.trim()} data-testid="prompt-tidy-button"><Wand2 className="!h-4 !w-4" /> {tidying ? "Tidying" : "Tidy this prompt"}</Button>
            </div>
            <Textarea id="prompt-text" value={form.text} maxLength={4000} rows={7} onChange={(e) => set("text", e.target.value)} className="rounded-xl" data-testid="prompt-text-input" />
            <p className="text-right text-xs text-muted-foreground">{form.text.length} of 4,000</p>
          </div>
          {suggestion && (
            <div className="rounded-xl border border-action/50 bg-action/5 p-4" data-testid="prompt-tidy-suggestion">
              <p className="text-sm font-medium">Suggested rewrite</p>
              <p className="mt-1 text-xs text-muted-foreground">Check it keeps every fact you typed. Nothing changes until you accept.</p>
              <p className="mt-3 whitespace-pre-wrap text-sm" data-testid="prompt-tidy-text">{suggestion}</p>
              <div className="mt-3 flex gap-2">
                <Button type="button" size="sm" onClick={() => { set("text", suggestion); setSuggestion(""); }} data-testid="prompt-tidy-accept">Accept</Button>
                <Button type="button" size="sm" variant="ghost" onClick={() => setSuggestion("")} data-testid="prompt-tidy-discard">Discard</Button>
              </div>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="prompt-for">For</Label><ForSelect id="prompt-for" value={form.for_id} onChange={(v) => set("for_id", v)} testid="prompt-for-select" /></div>
            <div className="space-y-2"><Label htmlFor="prompt-campaign">Campaign (optional)</Label><Input id="prompt-campaign" value={form.campaign} maxLength={120} onChange={(e) => set("campaign", e.target.value)} data-testid="prompt-campaign-input" /></div>
          </div>
          <fieldset>
            <legend className="text-sm font-medium">Channels</legend>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {CHANNELS.map((c) => (
                <button key={c} type="button" aria-pressed={form.channels.includes(c)} onClick={() => toggleChannel(c)} data-testid={`prompt-channel-${c.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cn("rounded-full border px-2.5 py-1 text-xs transition-quick hover:bg-muted", form.channels.includes(c) && "border-foreground bg-foreground text-background hover:bg-foreground")}>{c}</button>
              ))}
            </div>
          </fieldset>
          <div className="space-y-2"><Label htmlFor="prompt-why">Why it worked (optional)</Label><Textarea id="prompt-why" value={form.why_it_worked} maxLength={500} rows={2} onChange={(e) => set("why_it_worked", e.target.value)} className="rounded-xl" data-testid="prompt-why-input" /></div>
          <fieldset>
            <legend className="text-sm font-medium">Your result rating</legend>
            <div className="mt-2 flex gap-1.5" role="radiogroup">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" role="radio" aria-checked={form.rating === n} onClick={() => set("rating", n)} data-testid={`prompt-rating-${n}`}
                  className={cn("h-9 w-9 rounded-lg border text-sm transition-quick hover:bg-muted", form.rating === n && "border-foreground font-semibold")}>{n}</button>
              ))}
            </div>
          </fieldset>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="prompt-form-cancel">Cancel</Button>
            <Button type="submit" disabled={busy || !form.title.trim() || !form.text.trim()} data-testid="prompt-form-save">{busy ? "Saving" : "Save prompt"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
